import { NoImage } from 'macaron-frontend-ui'
import Image from '~/components/image/Image'
import { format } from '~/lib/date/date'
import type {
  PlanDetailChildMenuItemFragment,
  PlanDetailOptionItemFragment,
} from '~/lib/graphql/graphql'
import { money } from '~/lib/money/money'
import { useIsPC } from '~/lib/ua'
import { sva } from '~/styled-system/css'

type Props = {
  menu: PlanDetailChildMenuItemFragment | PlanDetailOptionItemFragment
}

const styles = sva({
  slots: [
    'container',
    'content',
    'text',
    'menu',
    'title',
    'price',
    'description',
    'notice',
    'period',
    'label',
  ],
  base: {
    container: {
      bgColor: 'white',
      p: '3',
      rounded: 'md',
      _pc: {
        border: 'medium',
      },
    },
    content: {
      display: 'flex',
      alignItems: 'start',
      justifyContent: 'start',
      spaceX: '2',
      _pc: {
        spaceX: '3',
      },
    },
    text: {
      flex: '1',
      spaceY: '1',
      _pc: {
        spaceY: '2',
      },
    },
    menu: {
      _pc: {
        display: 'flex',
        alignItems: 'start',
        justifyContent: 'space-between',
        spaceY: '0',
        columnGap: '4',
      },
    },
    title: {
      fontSize: 'base',
      fontWeight: 'bold',
      _pc: {
        fontSize: 'md',
      },
    },
    price: {
      textAlign: 'right',
      fontSize: 'md',
      _pc: {
        fontSize: 'lg',
        flexShrink: '0',
        width: '[70px]',
      },
    },
    description: {
      fontSize: 'sm',
      whiteSpace: 'pre-wrap',
      color: 'gray.700',
      _pc: {
        color: 'gray.800',
      },
    },
    notice: {
      mt: '2',
      fontSize: 'sm',
      color: 'gray.700',
      _pc: {
        fontSize: 'sm',
      },
    },
    period: {
      mt: '1',
      display: 'flex',
      alignItems: 'center',
      _pc: {
        mt: '2',
      },
    },
    label: {
      fontSize: 'xs',
      mr: '1',
      bgColor: 'background',
      p: '1',
      fontWeight: 'bold',
      lineHeight: 'none',
      color: 'gray.700',
      _pc: {
        py: '1',
        px: '2',
      },
    },
  },
})

export function OptionChildMenu({ menu }: Props) {
  const isPC = useIsPC()
  const classes = styles()
  const imageWidth = isPC ? 90 : 70
  const imageHeight = isPC ? 90 : 70
  return (
    <section className={classes.container}>
      <div className={classes.content}>
        {menu.image ? (
          <Image
            src={menu.image.url}
            alt={menu.image.name}
            width={imageWidth}
            height={imageHeight}
            className="rounded"
          />
        ) : (
          <NoImage width={imageWidth} height={imageHeight} />
        )}
        <div className={classes.text}>
          <div className={classes.menu}>
            <p className={classes.title}>{menu.name}</p>
            <p className={classes.price}>{menu.price ? `${money(menu.price)}円` : '無料'}</p>
          </div>
          <div>
            <p className={classes.description}>{menu.description.trim()}</p>
            {(menu.visitingPeriodFrom !== null || menu.visitingPeriodTo !== null) && (
              <p className={classes.period}>
                <span className={classes.label}>来店期間</span>
                {menu.visitingPeriodFrom !== null &&
                  format(menu.visitingPeriodFrom, 'yyyy年M月d日(E)')}
                〜
                {menu.visitingPeriodTo !== null && format(menu.visitingPeriodTo, 'yyyy年M月d日(E)')}
              </p>
            )}
            {menu.closeDays > 0 && (
              <p className={classes.notice}>
                ※{menu.__typename === 'ChildMenu' && '当メニュー'}
                {menu.__typename === 'OptionMenu' && '当オプション'}
                {`は来店日の${menu.closeDays}日前まで予約可能です`}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
